import { VolumeX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useChannelSettingsContext } from '@sendbird/uikit-react/ChannelSettings/context';
import { useTranslate } from 'ra-core';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Role } from '@sendbird/chat';
import { useGetUsers } from '../../../hooks/useGetUsers';
import { type User, UserInfiniteList } from '../UserInfiniteList';
import { UserCard } from '../UserCard';
import { SearchTextInput } from '../SearchTextInput';
import { useFetchChannelOperators } from '../../../api/hooks/sendbird/useFetchChannelOperators';
import AllMembersModalDotsMenu from './AllMembersModalDotsMenu';
import { useFetchMutedUsers } from '../../../api/hooks/sendbird/useFetchMutedUsers';
import { Button } from '../../../components/ui/button';
import { ChannelSettingsModal } from './ChannelSettingsModal';

export interface AllMembersModalProps {
  open: boolean;
  onCancel(): void;
}

export function AllMembersModal({ onCancel, open }: AllMembersModalProps) {
  const translate = useTranslate();
  const { channel } = useChannelSettingsContext();
  const { data: channelOperators } = useFetchChannelOperators();
  const { data: mutedUsers } = useFetchMutedUsers();
  const [searchTerm, setSearchTerm] = useState('');

  const {
    data: users,
    isLoading,
    isFetchingNextPage,
    refetch,
    fetchNextPage,
    hasNextPage,
    error,
    isError,
    isFetchNextPageError,
    isRefetching,
  } = useInfiniteQuery(
    useGetUsers({ query: searchTerm, memberOfChannel: channel.url })
  );

  const flattenedUsers = useMemo(() => {
    if (!users?.pages) return [];

    return users.pages
      .flatMap((page) => page.data)
      .map((user) => ({ ...user, userId: user.id.toString() }));
  }, [users]);

  const operatorIds = useMemo(
    () => new Set((channelOperators ?? []).map((operator) => operator.userId)),
    [channelOperators]
  );
  const mutedUserIds = useMemo(
    () => new Set((mutedUsers ?? []).map((user) => user.userId)),
    [mutedUsers]
  );

  return (
    <ChannelSettingsModal
      open={open}
      onCancel={onCancel}
      title={translate('chat.allMembersModalTitle')}
      header={
        <SearchTextInput
          label={translate('chat.searchMembers')}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
        />
      }
      footer={
        <Button type="button" variant="outline" onClick={onCancel}>
          {translate('ra.action.cancel')}
        </Button>
      }
    >
      <UserInfiniteList
        users={flattenedUsers}
        isLoadingUsers={isFetchingNextPage}
        isPendingUsers={isLoading}
        refetchUsers={refetch}
        fetchNextPageUsers={fetchNextPage}
        errorUsers={error}
        isErrorUsers={isError}
        isFetchNextPageError={isFetchNextPageError}
        isRefetching={isRefetching}
        hasNextPageUsers={hasNextPage}
        renderUser={(user: User) => (
          <UserCard
            internalId={user.internalId}
            department={user.department}
            displayName={user.displayName}
            jobTitle={user.jobTitle}
            pictureUrl={user.pictureUrl}
            units={user.units}
            right={
              <>
                {operatorIds.has(user.userId) && (
                  <span className="text-xs text-muted-foreground">
                    {translate('chat.operator')}
                  </span>
                )}
                {mutedUserIds.has(user.userId) && (
                  <VolumeX
                    className="size-6 text-primary"
                    aria-label={translate('chat.muted')}
                  />
                )}
                {channel.myRole === Role.OPERATOR && (
                  <AllMembersModalDotsMenu user={user} />
                )}
              </>
            }
          />
        )}
      />
    </ChannelSettingsModal>
  );
}
